import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import DeleteButton from '../../common/DeleteButton'
import InfoTooltip from '../../common/InfoTooltip'

import {choiceTypes, questionTypeOptions} from '../../constants/NewTest'
import utils from '../utils'
import {PLACE_AFTER_START, buildPlaceAfterOptions} from './reorderUtils'

import styles from '../styles/TestSetup.styles'
import MultipleChoiceOptions from './MultipleChoiceOptions'
import FaultPercentageOptions from './FaultPercentageOptions'
import FollowUpQuestion from './FollowUpQuestion'

const SelectIcon = withStyles(theme => ({
  root: {
    marginRight: theme.spacing.unit / 2,
    color: theme.palette.grey[800],
  },
}))(ExpandMoreIcon)

const PLACE_AFTER_STATUS_TIMEOUT = 3000

const QuestionForm = props => {
  const {
    classes,
    testType,
    question,
    questions,
    setQuestion,
    removeQuestion,
    setChoiceType,
    setChoice,
    addChoice,
    removeChoice,
    currentQuestion,
    questionError,
    validateQuestion,
    choiceErrors,
    validateFields,
    disableInputs,
    maxAnswers,
    followUp,
    addFollowUp,
    removeFollowUp,
    setFollowUpType,
    setFollowUpQuestion,
    followUpError,
    placeQuestionAfter,
    textInputRef,
  } = props

  const [placeAfterStatus, setPlaceAfterStatus] = useState('')
  const placeAfterStatusTimer = useRef(null)

  useEffect(() => () => clearTimeout(placeAfterStatusTimer.current), [])

  const handlePlaceAfterChange = ({target}) => {
    const newIndex = placeQuestionAfter(target.value)

    if (newIndex > -1) {
      setPlaceAfterStatus(`Moved to position ${newIndex + 1} of ${questions.length}.`)
      clearTimeout(placeAfterStatusTimer.current)
      placeAfterStatusTimer.current = setTimeout(
        () => setPlaceAfterStatus(''),
        PLACE_AFTER_STATUS_TIMEOUT,
      )
    }
  }

  const getOptions = () => {
    switch (question.choiceType) {
      case choiceTypes.multipleChoice:
        return (
          <MultipleChoiceOptions
            question={question}
            disableInputs={disableInputs}
            maxAnswers={maxAnswers}
            addChoice={addChoice}
            removeChoice={removeChoice}
            validateFields={validateFields}
            currentQuestion={currentQuestion}
            setChoice={setChoice}
            choiceErrors={choiceErrors}
          />
        )
      case choiceTypes.totalFaultPercentage:
        return (
          <FaultPercentageOptions
            question={question}
            disableInputs={disableInputs}
            maxAnswers={maxAnswers}
            addChoice={addChoice}
            removeChoice={removeChoice}
            validateFields={validateFields}
            currentQuestion={currentQuestion}
            setChoice={setChoice}
            choiceErrors={choiceErrors}
          />
        )
      default:
        return null
    }
  }

  return (
    <Grid container className={classes.questionFormContainer}>
      <Grid container className={classes.questionHeader} style={{alignItems: 'center'}}>
        <Typography variant="body1" className={classes.sectionSubtitle}>
          {`Question - ${currentQuestion + 1}`}
        </Typography>

        {!disableInputs && <DeleteButton onClick={removeQuestion} />}
      </Grid>
      {/* Positioning is not content: this control stays enabled for
          read-only default questions, outside the disableInputs scope */}
      <Grid className={classes.placeAfterSection}>
        <Typography variant="body1" className={classes.sectionSubtitle}>
          Place Question After
        </Typography>
        <Select
          style={{overflow: 'initial'}}
          className={classes.selectRoot}
          classes={{
            icon: classes.selectIcon,
            selectMenu: classes.selectMenu,
          }}
          value={currentQuestion === 0 ? PLACE_AFTER_START : currentQuestion - 1}
          variant="outlined"
          IconComponent={SelectIcon}
          onChange={handlePlaceAfterChange}
          input={
            <OutlinedInput labelWidth={0} classes={{notchedOutline: classes.notchedOutline}} />
          }
          SelectDisplayProps={{'aria-label': 'Place Question After'}}
        >
          {buildPlaceAfterOptions(questions, currentQuestion).map(({value, label}) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
        <Typography
          className={classes.placeAfterStatus}
          role="status"
          aria-live="polite"
          component="div"
        >
          {placeAfterStatus}
        </Typography>
      </Grid>
      <Typography
        variant="body1"
        className={classes.sectionSubtitle}
        style={{width: 'max-content', position: 'relative'}}
      >
        Question Type
        {question.choiceType === choiceTypes.openEndedNumeric && (
          <InfoTooltip
            text={
              <div>
                <span>This is an open-ended question that will only accept numeric answers.</span>
                <br />
                <br />
                <span>Examples of valid responses:</span>
                <ul style={{margin: '8 8 0 0', paddingLeft: 20, lineHeight: '24px'}}>
                  <li>Age: 25</li>
                  <li>Rating (1-10): 7</li>
                  <li>Percentage: 85</li>
                </ul>
              </div>
            }
            placement="right"
          />
        )}
      </Typography>
      <Select
        style={{overflow: 'initial'}}
        className={classes.selectRoot}
        classes={{
          icon: `${classes.selectIcon} ${disableInputs ? classes.disabledIcon : ''}`,
          selectMenu: classes.selectMenu,
        }}
        value={question.choiceType}
        variant="outlined"
        IconComponent={SelectIcon}
        onChange={({target}) => {
          setChoiceType(target.value)
        }}
        input={<OutlinedInput labelWidth={0} classes={{notchedOutline: classes.notchedOutline}} />}
        disabled={disableInputs}
      >
        {questionTypeOptions
          .filter(({value}) => utils.isQuestionTypeAllowedForTestType(value, testType))
          .map(({value, label}) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
      </Select>

      <TextField
        value={question.question}
        onChange={({target}) => {
          setQuestion(target.value.trimStart())
          validateQuestion()
        }}
        variant="outlined"
        className={classes.nameInput}
        inputRef={textInputRef}
        InputProps={{
          classes: {
            root: classes.nameInputOutline,
            notchedOutline: questionError ? classes.errorOutline : classes.notchedOutline,
          },
          onBlur: () => {
            setQuestion(question.question.trim())
            validateQuestion()
          },
          readOnly: disableInputs,
        }}
        placeholder="Type your question"
        helperText={questionError}
        FormHelperTextProps={{
          className: classes.inputErrorText,
          errormessage: 'true',
        }}
        multiline
        disabled={disableInputs}
      />

      {getOptions()}

      {question.choiceType === choiceTypes.multipleChoice && (
        <FollowUpQuestion
          followUp={followUp}
          disableInputs={disableInputs}
          addFollowUp={addFollowUp}
          removeFollowUp={removeFollowUp}
          setFollowUpType={setFollowUpType}
          setFollowUpQuestion={setFollowUpQuestion}
          followUpError={followUpError}
          validateFields={validateFields}
          currentQuestion={currentQuestion}
        />
      )}
    </Grid>
  )
}

QuestionForm.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  testType: PropTypes.string.isRequired,
  question: PropTypes.shape({
    question: PropTypes.string,
    choiceType: PropTypes.number,
    choices: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  setQuestion: PropTypes.func.isRequired,
  removeQuestion: PropTypes.func.isRequired,
  setChoiceType: PropTypes.func.isRequired,
  setChoice: PropTypes.func.isRequired,
  addChoice: PropTypes.func.isRequired,
  removeChoice: PropTypes.func.isRequired,
  currentQuestion: PropTypes.number.isRequired,
  questionError: PropTypes.string.isRequired,
  validateQuestion: PropTypes.func.isRequired,
  choiceErrors: PropTypes.objectOf(PropTypes.string).isRequired,
  validateFields: PropTypes.func.isRequired,
  disableInputs: PropTypes.bool.isRequired,
  followUp: PropTypes.shape({
    choiceType: PropTypes.number,
    question: PropTypes.string,
  }),
  addFollowUp: PropTypes.func.isRequired,
  removeFollowUp: PropTypes.func.isRequired,
  setFollowUpType: PropTypes.func.isRequired,
  setFollowUpQuestion: PropTypes.func.isRequired,
  followUpError: PropTypes.string,
  placeQuestionAfter: PropTypes.func.isRequired,
  textInputRef: PropTypes.shape({current: PropTypes.any}),
}

QuestionForm.defaultProps = {
  followUp: null,
  followUpError: '',
  textInputRef: null,
}

export default withStyles(styles)(QuestionForm)
